/**
 * bogos-theme-bridge.js
 *
 * Puente entre la app BOGOS (Free Gift) y el sistema interno del tema Minimog.
 *
 * Problema:
 *   BOGOS detecta el tema como "FoxEcom" e intenta llamar a
 *     theme.onCartDrawerUpdate(cart)
 *   para refrescar el cart drawer cuando añade/elimina un regalo.
 *   El tema Minimog no expone esa función, así que BOGOS lanza:
 *     TypeError: e?.onCartDrawerUpdate is not a function
 *   y cae en su fallback ("Can not fetch all body").
 *
 * Solución:
 *   1. Exponer window.theme.onCartDrawerUpdate como punto de entrada.
 *   2. NO usar el cart que pasa BOGOS: no incluye `sections` renderizadas
 *      por la Section Rendering API de Shopify, que es lo que necesitan
 *      los componentes <m-cart-count> y <m-cart-items> para repintar.
 *   3. Pedir el cart + las secciones y emitir el pub-sub interno (cartUpdate).
 *
 * ⛔ TRANSPORTE — NUNCA POST A /cart/update.js (bucle infinito, incidente
 *    2026-07-20):
 *   BOGOS OBSERVA ese endpoint (consola: `cart-events observer:/cart/update.js`).
 *   Si el puente refresca con POST /cart/update.js se cierra un ciclo:
 *     puente POST → observer de BOGOS → checkEventCart → refreshCart →
 *     renderCart → theme.onCartDrawerUpdate() → puente POST → ∞
 *   El drawer temblaba sin parar, el /cart se repintaba solo, el popup de
 *   regalos no podía guardar y la tienda acababa en HTTP 429.
 *
 *   Transporte correcto (el mismo que usa el propio script de integración de
 *   BOGOS en su app embed, `sca_fg_custom_script`, para `fg-gifts:updated`):
 *     GET /cart.js                → el objeto cart
 *     GET /?sections=a,b,c        → las secciones renderizadas
 *   Dos GET a endpoints que BOGOS no observa. Ojo: GET /cart.js IGNORA el
 *   parámetro ?sections= (por eso hacen falta las dos peticiones; el error
 *   de PR #11 fue "arreglarlo" pasando a POST en vez de separar las llamadas).
 *
 * Antirebote y race conditions:
 *   - BOGOS puede llamar varias veces seguidas (al añadir y al quitar
 *     regalos en cadena). Si lanzamos varias peticiones en paralelo, la
 *     última en completar puede traer un estado obsoleto.
 *   - Por eso usamos:
 *       a) DEBOUNCE: agrupamos llamadas seguidas en una única petición
 *          (150 ms) tras la última señal de BOGOS.
 *       b) SECUENCIA "última petición gana": cada petición lleva un nº;
 *          solo emite la más reciente (las respuestas viejas se descartan).
 *       c) COOLDOWN de reentrada: se ignoran las señales que llegan justo
 *          después de haber emitido nosotros (cinturón y tirantes por si
 *          alguna app vuelve a llamarnos como eco de nuestro repintado).
 *
 * XMLHttpRequest y NO fetch: BOGOS overrida window.fetch (fetch-override) y
 * en plena digestión de un add puede TRAGARSE la promesa — el producto
 * entraba pero el drawer nunca repintaba. XHR queda fuera de su override.
 */
(function () {
  'use strict';

  var DEFAULT_SECTIONS = ['cart-drawer', 'cart-template', 'cart-count'];
  var DEBOUNCE_MS = 150;
  var COOLDOWN_MS = 600;

  var refreshTimer = null;
  var requestSeq = 0;
  var lastEmitAt = 0;

  function shopRoot() {
    try {
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      return r || '/';
    } catch (e) { return '/'; }
  }

  // Marcador oficial de exclusión de BOGOS. En su `gift.min.js` descarta toda
  // URL que contenga `_bogos-ignore` en las DOS vías con las que espía el
  // carrito: el override de `window.fetch` y el PerformanceObserver de
  // `listenAPI`. Sin esto, cada lectura del carrito que hacemos aquí le
  // dispara una ronda `checkEventCart → checkAllOffers → auto add all gifts →
  // POST /cart/update.js`, y su repintado nos hace leer otra vez: realimentación.
  function quiet(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + '_bogos-ignore=1';
  }

  function getRequestedSections() {
    var sections = [];
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sections }
        })
      );
    } catch (e) {}

    // Secciones de los componentes de carrito PRESENTES en la página: el
    // drawer expone <m-cart-items data-section-name="cart-drawer"> — sin su
    // sección renderizada, m-cart-items no puede repintar las líneas (bug
    // "añado del upsell y el sidecart no se refresca", reportado por Toti).
    try {
      document.querySelectorAll('m-cart-items[data-section-name], m-cart-count[data-section-name]').forEach(function (el) {
        var n = el.getAttribute('data-section-name');
        if (n && sections.indexOf(n) === -1) sections.push(n);
      });
    } catch (e) {}

    if (!sections.length) sections = DEFAULT_SECTIONS.slice();

    DEFAULT_SECTIONS.forEach(function (s) {
      if (sections.indexOf(s) === -1) sections.push(s);
    });

    return sections;
  }

  function emitCartUpdate(cart) {
    try {
      if (
        window.MinimogEvents &&
        window.MinimogTheme &&
        window.MinimogTheme.pubSubEvents &&
        window.MinimogTheme.pubSubEvents.cartUpdate
      ) {
        lastEmitAt = Date.now();
        window.MinimogEvents.emit(
          window.MinimogTheme.pubSubEvents.cartUpdate,
          { cart: cart }
        );
      }
    } catch (e) {}
  }

  // GET por XHR (fuera del alcance del fetch-override de BOGOS).
  function getJSON(url, cb) {
    try {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) {}
        cb(data);
      };
      xhr.onerror = function () { cb(null); };
      xhr.send();
    } catch (e) { cb(null); }
  }

  function doFetch() {
    refreshTimer = null;
    var seq = ++requestSeq;
    var sections = getRequestedSections();
    var root = shopRoot();

    getJSON(quiet(root + 'cart.js'), function (cart) {
      if (seq !== requestSeq || !cart) return; // llegó otra señal después
      getJSON(quiet(root + '?sections=' + encodeURIComponent(sections.join(','))), function (secs) {
        if (seq !== requestSeq) return;
        if (secs) cart.sections = secs;
        emitCartUpdate(cart);
      });
    });
  }

  function refreshCart() {
    // Eco de nuestro propio repintado: ignorar.
    if (Date.now() - lastEmitAt < COOLDOWN_MS) return;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(doFetch, DEBOUNCE_MS);
  }

  window.theme = window.theme || {};
  if (typeof window.theme.onCartDrawerUpdate !== 'function') {
    window.theme.onCartDrawerUpdate = function () {
      refreshCart();
    };
  }

  // ───────────────────────────────────────────────────────────────────────────
  // CORTAFUEGOS DEL BUCLE INFINITO DE BOGOS  (incidente 2026-07-20)
  //
  // BOGOS entra en un bucle propio en cuanto se conceden los primeros regalos.
  // Verificado quitando TODO nuestro código de la página: el bucle continúa
  // exactamente igual (7 vueltas en 20 s). No lo causamos nosotros.
  //
  // El ciclo, leído en su `gift.min.js`:
  //   1. procesa regalos con  POST /cart/update.js?sca_process_gifts
  //   2. `refreshCart` empieza con  `b.arrSaveAllFetch = []`  ← borra la lista
  //      con la que se protege de reaccionar a sus PROPIAS peticiones
  //   3. su PerformanceObserver de `listenAPI` recibe (tarde, en lote) la
  //      entrada de esa misma petición; el guard
  //         `!(d === 'fetch-override:' + endpoint)`
  //      ya no se cumple porque la lista está vacía
  //   4. → `checkEventCart(['observer:/cart/update.js'])` → `checkAllOffers`
  //      → `auto add all gifts from {…}` → vuelta al punto 1, sin fin
  // Consola: `observer:/cart/update.js` + `auto add all gifts` + `Can not fetch
  // all body` en bucle, hasta que la tienda entera responde HTTP 429.
  //
  // ARREGLO: su propio código ya contempla marcar esa petición como ignorable
  //   `sca_process_gifts${l ? "_bogos-ignore" : ""}`
  // y construye la URL desde una variable suya:
  //   `${FGSECOMAPP.variables.Shopify?.sca_update_url ?? "/cart/update.js"}?${g}`
  // Así que le añadimos el marcador a ESA variable: su POST de regalos deja de
  // disparar su propio observer y el ciclo se rompe en la primera vuelta.
  //
  // Los cambios REALES del carrito (add/change del cliente) siguen llegando a
  // BOGOS por las vías de siempre → los regalos se conceden igual. Medido en el
  // tema de preview cruzando los tres tramos:
  //   40€  → 7 vueltas/20s  ⇒  1 sola pasada, regalo concedido
  //   60€  → 0 vueltas, llegan Zero sauce + Crema de almendra
  //   90€  → 0 vueltas, 0 recargas de página, popup de elección intacto
  //
  // Si algún día BOGOS corrige su carrera, esto se vuelve inofensivo (solo
  // añade un parámetro que Shopify ignora). Si cambian el nombre de la
  // variable, el parche deja de aplicarse sin romper nada.
  // ── Parte 2: la RECARGA DURA de /cart ──────────────────────────────────────
  //
  // En la página del carrito BOGOS no repinta: recarga entera. En `refreshCart`:
  //   `t && (isCartPage || isCheckout) && location.reload()`
  // Como en cada carga vuelve a procesar regalos y vuelve a declarar "he
  // actualizado", el resultado es recarga tras recarga (~1,3 s) — el `/cart` se
  // refrescaba solo sin parar y no daba tiempo ni a usar el popup de regalos.
  //
  // `isCartPage` SOLO se usa para eso: se define como
  // `window.location.href.includes("/cart")` y aparece únicamente en los dos
  // sitios que deciden `location.reload()` (verificado sobre su `gift.min.js`:
  // 3 apariciones, una es la definición). Poniéndolo a false, BOGOS usa su
  // camino suave `renderCart()`, que dispara `fg-gifts:updated` → el script de
  // integración de la propia app pide `GET /cart.js` + `GET /?sections=` y
  // emite `cartUpdate` → `<m-cart-items data-section-name="cart-template">`
  // repinta `#MinimogCartBody` en el sitio. Es EXACTAMENTE el mismo camino que
  // ya funciona en el resto de páginas.
  //
  // Se hace solo si el tema tiene con qué repintar (MinimogEvents + pub/sub);
  // si no, se deja la recarga de BOGOS antes que dejar un carrito obsoleto.
  function canRepaintInPlace() {
    return !!(window.MinimogEvents && window.MinimogTheme &&
              window.MinimogTheme.pubSubEvents &&
              window.MinimogTheme.pubSubEvents.cartUpdate);
  }

  // Los dos parches se re-aseguran durante un rato: BOGOS reconstruye
  // `FGSECOMAPP.variables` con un spread durante su init y podría pisarlos.
  // Una excepción transitoria NO mata el timer (hallazgo de la auditoría
  // 2026-07-28: una carga lenta de gift.min.js reabría el bucle) y la ventana
  // se amplía a ~60 s por conexiones lentas.
  function armBogosLoopBreaker() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      try {
        var F = window.FGSECOMAPP;
        var vars = F && F.variables;
        var sh = vars && vars.Shopify;

        if (sh) {
          var url = typeof sh.sca_update_url === 'string' && sh.sca_update_url
            ? sh.sca_update_url
            : '/cart/update.js';
          if (url.indexOf('_bogos-ignore') === -1) {
            // El sufijo `&sca=` deja limpio el `?sca_process_gifts` que BOGOS
            // concatena justo después.
            sh.sca_update_url = url + (url.indexOf('?') === -1 ? '?' : '&') + '_bogos-ignore=1&sca=';
          }
        }

        if (vars && vars.isCartPage === true && canRepaintInPlace()) {
          vars.isCartPage = false;
        }
      } catch (e) { /* transitorio: seguir re-asegurando */ }
      if (tries > 1200) clearInterval(timer); // ~60 s re-asegurando
    }, 50);
  }
  armBogosLoopBreaker();

  // ───────────────────────────────────────────────────────────────────────────
  // SANITIZADOR DE OFERTAS CORRUPTAS  (incidente 2026-07-28, oferta 179052)
  //
  // El admin de BOGOS permite guardar una oferta con el mismo regalo duplicado
  // o con un gift_item cuyo clon ya no existe (`variant_shopify_id: null`).
  // Con datos así BOGOS nunca "ve" los regalos satisfechos: bucle de auto-add
  // (spam `auto add all gifts from {id}`, 429) y, en el click de checkout, su
  // revalidación concluye que sobra un regalo y hace location.reload() — el
  // cliente no puede pagar. Como los regalos los editan varias personas, esto
  // volverá a pasar: se sanea EN CLIENTE antes de que BOGOS procese.
  //
  // Reglas (solo se elimina lo demostrablemente roto):
  //   - gift_item sin variant_shopify_id (clon roto) → fuera
  //   - segundo gift_item para la MISMA variante original (Shopify fusiona las
  //     líneas y BOGOS no converge) → fuera, se conserva el primero válido
  //   - gift_products[].variants y gift_settings (auto_add_all_gifts_quantity,
  //     original_products_to_clone) se filtran en consecuencia
  //
  // Se sanea window.fgData.offers (fuente que BOGOS clona en su init) y
  // FGSECOMAPP.offers (su copia) si ya existía al llegar nosotros. Señal:
  // console.warn + evento GA4 `pb_offer_corrupt` + CustomEvent
  // `bogos:offer-corrupt` — el vigilante externo avisa a quien toque.
  function ga4(name, params) {
    try {
      var p = params || {};
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, p);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, p));
      }
    } catch (e) {}
  }

  function sanitizeOffer(o) {
    var removed = [];
    if (!o || o._qmtxSane) return removed;

    var keepClone = Object.create(null);     // clones supervivientes
    var validOriginal = Object.create(null); // originales con clon válido

    if (Array.isArray(o.gift_items)) {
      var seenOriginal = Object.create(null);
      var clean = o.gift_items.filter(function (g) {
        if (!g) return false;
        if (!g.variant_shopify_id) {
          removed.push('clon roto (variante null): ' + (g.variant_title || g.id));
          return false;
        }
        var key = String(g.original_variant_shopify_id || g.variant_shopify_id);
        if (seenOriginal[key]) {
          removed.push('regalo duplicado (misma variante original): ' + (g.variant_title || g.id));
          return false;
        }
        seenOriginal[key] = 1;
        keepClone[String(g.variant_shopify_id)] = 1;
        validOriginal[key] = 1;
        return true;
      });
      if (removed.length) o.gift_items = clean;
    }

    if (removed.length) {
      try {
        if (Array.isArray(o.gift_products)) {
          o.gift_products.forEach(function (p) {
            if (p && Array.isArray(p.variants)) {
              p.variants = p.variants.filter(function (v) {
                return v && v.variant_shopify_id && keepClone[String(v.variant_shopify_id)];
              });
            }
          });
          o.gift_products = o.gift_products.filter(function (p) {
            return p && (!Array.isArray(p.variants) || p.variants.length);
          });
        }
        var gs = o.gift_settings;
        if (gs) {
          if (Array.isArray(gs.auto_add_all_gifts_quantity)) {
            gs.auto_add_all_gifts_quantity = gs.auto_add_all_gifts_quantity.filter(function (q) {
              return q && validOriginal[String(q.id)];
            });
          }
          if (Array.isArray(gs.original_products_to_clone)) {
            gs.original_products_to_clone = gs.original_products_to_clone.filter(function (pc) {
              if (!pc) return false;
              if (Array.isArray(pc.variants_ids)) {
                pc.variants_ids = pc.variants_ids.filter(function (id) { return validOriginal[String(id)]; });
              }
              if (Array.isArray(pc.variants_qty)) {
                pc.variants_qty = pc.variants_qty.filter(function (q) { return q && validOriginal[String(q.id)]; });
              }
              return Array.isArray(pc.variants_ids) ? pc.variants_ids.length > 0 : true;
            });
          }
        }
      } catch (e) {}
    }

    o._qmtxSane = true;
    return removed;
  }

  var reportedOffers = Object.create(null);
  function sanitizeOfferList(offers) {
    if (!Array.isArray(offers)) return;
    offers.forEach(function (o) {
      var removed = sanitizeOffer(o);
      if (removed.length && o && !reportedOffers[o.id]) {
        reportedOffers[o.id] = 1;
        try {
          console.warn('[bogos-bridge] Oferta corrupta saneada en cliente: "' +
            (o.title || o.id) + '" — ' + removed.join('; ') +
            '. Corrígela en el admin de BOGOS.');
        } catch (e) {}
        ga4('pb_offer_corrupt', { offer_id: o.id, offer_title: o.title || '', removed: removed.length });
        try {
          document.dispatchEvent(new CustomEvent('bogos:offer-corrupt', {
            detail: { offerId: o.id, title: o.title || '', removed: removed }
          }));
        } catch (e) {}
      }
    });
  }

  // B2B: BOGOS no pinta nada en el portal mayorista (regalos excluidos por tag
  // en 15/16 ofertas, y la oferta-link AFE añadiría regalos a 0 € a un pedido
  // B2B). El layout ya fuerza window.fgData.offers = [] con un setter; esto es
  // la segunda red por si BOGOS cambia cómo publica sus datos: vacía EN SITIO
  // las dos listas que lee. B2C: rama no tocada.
  function emptyOffersInPlace(list) {
    if (Array.isArray(list) && list.length) list.length = 0;
  }

  function armBogosOfferSanitizer() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      try {
        if (window.__qmtxB2B) {
          if (window.fgData) emptyOffersInPlace(window.fgData.offers);
          if (window.FGSECOMAPP) emptyOffersInPlace(window.FGSECOMAPP.offers);
        }
        if (window.fgData && Array.isArray(window.fgData.offers)) {
          sanitizeOfferList(window.fgData.offers);
        }
        var F = window.FGSECOMAPP;
        if (F && Array.isArray(F.offers)) {
          sanitizeOfferList(F.offers);
        }
      } catch (e) { /* transitorio: seguir vigilando */ }
      if (tries > 1200) clearInterval(timer); // ~60 s: cubre cargas lentas de gift.min.js
    }, 50);
  }
  armBogosOfferSanitizer();

  // Hook de diagnóstico/tests (usado por el vigilante y para probar en consola).
  window.__qmtxBogosBridge = { sanitizeOffer: sanitizeOffer };
})();
